import { Observable } from 'rxjs';
import { JwtInterceptor } from '../jwt/jwt.interceptor';
import { mergeMap } from 'rxjs/operators';

export declare class HttpHeaders {
	/**
	 * Internal map of lowercase header names to values.
	 */
	private headers;
	/**
	 * Internal map of lowercased header names to the normalized
	 * form of the name (the form seen first).
	 */
	private normalizedNames;
	/**
	 * Complete the lazy initialization of this object (needed before reading).
	 */
	private lazyInit;
	/**
	 * Queued updates to be materialized the next initialization.
	 */
	private lazyUpdate;
	constructor(
		headers?:
			| string
			| {
					[name: string]: string | string[];
			  }
	);
	/**
	 * Checks for existence of header by given name.
	 */
	has(name: string): boolean;
	/**
	 * Returns first header that matches given name.
	 */
	get(name: string): string | null;
	/**
	 * Returns the names of the headers
	 */
	keys(): string[];
	/**
	 * Returns list of header values for a given name.
	 */
	getAll(name: string): string[] | null;
	append(name: string, value: string | string[]): HttpHeaders;
	set(name: string, value: string | string[]): HttpHeaders;
	delete(name: string, value?: string | string[]): HttpHeaders;
	private maybeSetNormalizedName;
	private init;
	private copyFrom;
	private clone;
	private applyUpdate;
}
export declare enum HttpEventType {
	/**
	 * The request was sent out over the wire.
	 */
	Sent = 0,
	/**
	 * An upload progress event was received.
	 */
	UploadProgress = 1,
	/**
	 * The response status code and headers were received.
	 */
	ResponseHeader = 2,
	/**
	 * A download progress event was received.
	 */
	DownloadProgress = 3,
	/**
	 * The full response including the body was received.
	 */
	Response = 4,
	/**
	 * A custom event from an interceptor or a backend.
	 */
	User = 5
}

export declare abstract class HttpResponseBase {
	/**
	 * All response headers.
	 */
	readonly headers: HttpHeaders;
	/**
	 * Response status code.
	 */
	readonly status: number;
	/**
	 * Textual description of response status code.
	 *
	 * Do not depend on this.
	 */
	readonly statusText: string;
	/**
	 * URL of the resource retrieved, or null if not available.
	 */
	readonly url: string | null;
	/**
	 * Whether the status code falls in the 2xx range.
	 */
	readonly ok: boolean;
	/**
	 * Type of the response, narrowed to either the full response or the header.
	 */
	readonly type: HttpEventType.Response | HttpEventType.ResponseHeader;
	/**
	 * Super-constructor for all responses.
	 *
	 * The single parameter accepted is an initialization hash. Any properties
	 * of the response passed there will override the default values.
	 */
	constructor(
		init: {
			headers?: HttpHeaders;
			status?: number;
			statusText?: string;
			url?: string;
		},
		defaultStatus?: number,
		defaultStatusText?: string
	);
}
/**
 * A partial HTTP response which only includes the status and header data,
 * but no response body.
 *
 * `HttpHeaderResponse` is a `HttpEvent` available on the response
 * event stream, only when progress events are requested.
 *
 * @publicApi
 */
export declare class HttpHeaderResponse extends HttpResponseBase {
	/**
	 * Create a new `HttpHeaderResponse` with the given parameters.
	 */
	constructor(init?: { headers?: HttpHeaders; status?: number; statusText?: string; url?: string });
	readonly type: HttpEventType.ResponseHeader;
	/**
	 * Copy this `HttpHeaderResponse`, overriding its contents with the
	 * given parameter hash.
	 */
	clone(update?: { headers?: HttpHeaders; status?: number; statusText?: string; url?: string }): HttpHeaderResponse;
}

export declare class HttpResponse<T> extends HttpResponseBase {
	/**
	 * The response body, or `null` if one was not returned.
	 */
	readonly body: T | null;
	/**
	 * Construct a new `HttpResponse`.
	 */
	constructor(init?: { body?: T | null; headers?: HttpHeaders; status?: number; statusText?: string; url?: string });
	readonly type: HttpEventType.Response;
	clone(): HttpResponse<T>;
	clone(update: { headers?: HttpHeaders; status?: number; statusText?: string; url?: string }): HttpResponse<T>;
	clone<V>(update: { body?: V | null; headers?: HttpHeaders; status?: number; statusText?: string; url?: string }): HttpResponse<V>;
}

/**
 * A response that represents an error or failure, either from a
 * non-successful HTTP status, an error while executing the request,
 * or some other failure which occurred during the parsing of the response.
 *
 * Any error returned on the `Observable` response stream will be
 * wrapped in an `HttpErrorResponse` to provide additional context about
 * the state of the HTTP layer when the error occurred. The error property
 * will contain either a wrapped Error object or the error response returned
 * from the server.
 *
 * @publicApi
 */
export declare class HttpErrorResponse extends HttpResponseBase implements Error {
	stack?: string;
	readonly name: string;
	readonly message: string;
	readonly error: any | null;
	/**
	 * Errors are never okay, even when the status code is in the 2xx success range.
	 */
	//readonly ok = false;
	constructor(init: { error?: any; headers?: HttpHeaders; status?: number; statusText?: string; url?: string });
}

export class HttpClient {
	private _jwtInterceptor: JwtInterceptor;
	private _environmentUrl: string;
	constructor() {
		this._jwtInterceptor = new JwtInterceptor();
		this._environmentUrl = window.location.origin || window.location.protocol + '//' + window.location.hostname;

		if (this._environmentUrl.indexOf('localhost')) {
			// this._environmentUrl = 'http://172.25.99.109';
		}
	}

	private handleErrors(response) {
		if (!response.ok) {
			throw response as HttpErrorResponse;
		}
		return response;
	}

	post<T>(url: string, request: any | null, awaitingFullResponse = false): Observable<T> {
		url = this._environmentUrl + url;
		return this._jwtInterceptor.intercept(url).pipe(
			mergeMap(headers =>
				fetch(url, {
					method: 'POST',
					headers,
					body: JSON.stringify(request),
					credentials: 'include'
				})
					.then(this.handleErrors)
					.then(response => {
						return awaitingFullResponse ? response : response.json();
					})
			)
		);
	}

	get<T>(url: string, awaitingFullResponse = false): Observable<T> {
		url = this._environmentUrl + url;
		return this._jwtInterceptor.intercept(url).pipe(
			mergeMap(headers =>
				fetch(url, {
					method: 'GET',
					headers,
					credentials: 'include'
				})
					.then(this.handleErrors)
					.then(response => {
						return awaitingFullResponse ? response : response.json();
					})
			)
		);
	}

	put<T>(url: string, request: any, awaitingFullResponse = false): Observable<T> {
		url = this._environmentUrl + url;
		return this._jwtInterceptor.intercept(url).pipe(
			mergeMap(headers =>
				fetch(url, {
					method: 'PUT',
					headers,
					body: JSON.stringify(request),
					credentials: 'include'
				})
					.then(this.handleErrors)
					.then(response => {
						return awaitingFullResponse ? response : response.json();
					})
			)
		);
	}
}
